import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_TIME_LIMIT_MS,
  pickLocalized,
  questionTimeLimitMs,
  toPublicQuestion,
  type AnswerResult,
  type BinogQuestion,
  type HostCatchUp,
  type LanguageMode,
  type LeaderboardEntryWithDelta,
  type PublicQuestion,
  type ServerMessage,
} from "@binogi-livequiz/shared";
import { AnswerTile, TILE_LETTERS } from "../components/AnswerTile";
import { HostErrorBanner } from "../components/HostErrorBanner";
import { TopTimerBar } from "../components/TopTimerBar";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { Button } from "../components/ui/Button";
import { Leaderboard } from "../components/Leaderboard";
import { useHostAttach } from "../hooks/useHostAttach";
import { lobbyStateFromJoined } from "./HostLobby";
import { serverErrorKey } from "../lib/i18n";
import { loadHostSessionCode } from "../lib/session-storage";
import { useWebSocket } from "../lib/ws-context";

export interface RouteState {
  sessionCode: string;
  // Seeds round 1 before QUESTION_START arrives. Empty on the HostEntry
  // deep link, which has no questions to hand over.
  questions: BinogQuestion[];
  timeLimitMs: number;
  languageMode?: LanguageMode;
  availableLanguages?: string[];
  // Set when the lobby's attach found the quiz already running (a second
  // device, or a lobby reloaded mid-quiz): the round to open on, and when
  // the lobby received it.
  catchUp?: HostCatchUp;
  catchUpAt?: number;
  // How many students the lobby or the attach counted, so "Answered 0 / N"
  // shows the student count before the first ANSWER_RECEIVED.
  playerCount?: number;
}

type Phase = "active" | "ended";

interface RoundState {
  questionIndex: number;
  // From QUESTION_START: the server's count and time limit, not the route
  // state's, which the deep link leaves empty.
  totalQuestions: number;
  timeLimitMs: number;
  question: PublicQuestion;
  startedAt: number;
  phase: Phase;
  // null when this screen does not know how many answered: it picked the
  // quiz up after the question ended, and the catch-up carries no count.
  answeredCount: number | null;
  totalPlayers: number;
  correctOids: string[];
  results: AnswerResult[];
  leaderboard: LeaderboardEntryWithDelta[];
}

// The round a host catch-up describes, or null for a phase this screen
// does not show. `receivedAt` dates the remaining time; `playerCount` is
// the student count an ended question's catch-up, which carries no count, keeps
// for the next question.
function roundFromCatchUp(
  host: HostCatchUp,
  receivedAt: number,
  playerCount: number,
): RoundState | null {
  if (host.phase !== "question" && host.phase !== "ended") return null;
  const { start } = host;
  const timeLimitMs = questionTimeLimitMs(start);
  const round: RoundState = {
    questionIndex: start.questionIndex,
    totalQuestions: start.totalQuestions,
    timeLimitMs,
    question: start.question,
    startedAt: receivedAt - timeLimitMs,
    phase: "ended",
    answeredCount: null,
    totalPlayers: playerCount,
    correctOids: [],
    results: [],
    leaderboard: [],
  };
  if (host.phase === "ended") {
    return {
      ...round,
      correctOids: host.end.correctOids,
      results: host.end.results,
      leaderboard: host.end.leaderboard,
    };
  }
  return {
    ...round,
    // Back-dated so the timer shows the time already gone and runs out
    // when the server's deadline does.
    startedAt: receivedAt - (timeLimitMs - host.remainingMs),
    phase: "active",
    answeredCount: host.answers.answeredCount,
    totalPlayers: host.answers.totalPlayers,
  };
}

export default function HostQuestion() {
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWebSocket();
  const { t, i18n } = useTranslation();
  // No route state: open the session this tab hosts, like the HostEntry
  // deep link (no questions); the attach's catch-up supplies the round.
  // See "Where a host screen gets its session" in
  // docs/architecture/frontend-patterns.md.
  const routeState = location.state as RouteState | null;
  const state = useMemo<RouteState | null>(() => {
    if (routeState) return routeState;
    const code = loadHostSessionCode();
    return code
      ? { sessionCode: code, questions: [], timeLimitMs: DEFAULT_TIME_LIMIT_MS }
      : null;
  }, [routeState]);
  // The host renders the question in the same language students see — for
  // lockedEn that's "en", for studentChoice the host's own UI locale (which
  // is also typically the lesson's source language).
  const displayLang =
    state?.languageMode === "lockedEn" ? "en" : i18n.language;

  const [round, setRound] = useState<RoundState | null>(() => {
    // Route state describes the moment the lobby navigated here, on the
    // socket it had just attached. After a reload history.state still holds
    // it, but the quiz has moved on, so this screen waits for the server's
    // catch-up instead (the socket is not open yet on a fresh page).
    if (!state || ws.status !== "open") return null;
    if (state.catchUp) {
      return roundFromCatchUp(
        state.catchUp,
        state.catchUpAt ?? Date.now(),
        state.playerCount ?? 0,
      );
    }
    if (state.questions.length === 0) return null;
    const first = state.questions[0]!;
    return {
      questionIndex: 0,
      totalQuestions: state.questions.length,
      timeLimitMs: state.timeLimitMs,
      question: toPublicQuestion(first),
      startedAt: Date.now(),
      phase: "active",
      answeredCount: 0,
      totalPlayers: state.playerCount ?? 0,
      correctOids: [],
      results: [],
      leaderboard: [],
    };
  });
  // An i18n key, translated at render so a language switch re-renders it.
  const [error, setError] = useState<string | null>(null);
  // HostReview and HostEntry save the host token before the lobby opens,
  // so a re-attach here finds it in sessionStorage. Opened without route
  // state, the screen attaches even on an open socket, as the lobby does:
  // it did not arrive from a lobby that just attached it.
  const noToken = useHostAttach(state?.sessionCode, {
    attachOnMount: !routeState,
  });

  useEffect(() => {
    if (noToken) {
      setError("host.errors.no_token");
    }
  }, [noToken]);

  useEffect(() => {
    const msg = ws.lastMessage;
    if (!msg) return;
    handle(msg);
    ws.consume();
    function handle(m: ServerMessage) {
      switch (m.type) {
        case "QUESTION_START":
          setError(null);
          // A fresh round's QUESTION_START carries no count: keep the
          // student count the screen already knows, which ANSWER_RECEIVED,
          // PLAYER_JOINED and PLAYER_LEFT then keep current.
          // A repeated Start's reply carries the time left, not the
          // question's full limit: keep the full limit this screen already
          // knows and back-date the start, so the bar resumes part-way down.
          // The session has one time limit, so any earlier round's limit
          // (from a QUESTION_START or a catch-up) is the real one; route
          // state's is only a default when the screen opened without a
          // question list.
          setRound((prev) => {
            const remainingMs = questionTimeLimitMs(m);
            const fullMs =
              m.answeredCount === undefined
                ? remainingMs
                : Math.max(
                    remainingMs,
                    prev?.timeLimitMs ?? state?.timeLimitMs ?? remainingMs,
                  );
            const now = Date.now();
            return {
              questionIndex: m.questionIndex,
              totalQuestions: m.totalQuestions,
              timeLimitMs: fullMs,
              question: m.question,
              startedAt: now - (fullMs - remainingMs),
              phase: "active",
              answeredCount: m.answeredCount ?? 0,
              // Without route state questions, round 1 takes the student
              // count the lobby passed.
              totalPlayers:
                m.totalPlayers ?? prev?.totalPlayers ?? state?.playerCount ?? 0,
              correctOids: [],
              results: [],
              leaderboard: [],
            };
          });
          break;
        case "ANSWER_RECEIVED":
          setRound((prev) =>
            prev && prev.questionIndex === m.questionIndex
              ? {
                  ...prev,
                  answeredCount: m.answeredCount,
                  totalPlayers: m.totalPlayers,
                }
              : prev,
          );
          break;
        case "PLAYER_JOINED":
        case "PLAYER_LEFT":
          setRound((prev) =>
            prev ? { ...prev, totalPlayers: m.playerCount } : prev,
          );
          break;
        case "QUESTION_END":
          setError(null);
          setRound((prev) =>
            prev && prev.questionIndex === m.questionIndex
              ? {
                  ...prev,
                  phase: "ended",
                  correctOids: m.correctOids,
                  results: m.results,
                  leaderboard: m.leaderboard,
                }
              : prev,
          );
          break;
        case "SESSION_FINISHED":
          navigate("/host/results", {
            state: {
              finalLeaderboard: m.finalLeaderboard,
              availableLanguages: state?.availableLanguages,
            },
          });
          break;
        case "SESSION_JOINED":
          // The attach went through, after a reload or a dropped socket:
          // pick the quiz up where the class is.
          setError(null);
          // A reply for another session (this socket's attach from before
          // a "New session") says nothing about this one.
          if (!m.host || m.sessionCode !== state?.sessionCode) break;
          if (m.host.phase === "finished") {
            navigate("/host/results", {
              replace: true,
              state: {
                finalLeaderboard: m.host.finished.finalLeaderboard,
                availableLanguages: state?.availableLanguages,
              },
            });
          } else if (m.host.phase === "lobby") {
            // No question has started (a Start that never reached the
            // server): back to the lobby, which attaches again.
            navigate("/host/lobby", {
              replace: true,
              state: lobbyStateFromJoined(m, state),
            });
          } else {
            setRound(roundFromCatchUp(m.host, Date.now(), m.playerCount));
            // Opened without route state: put the session's language
            // settings where the lobby would have, so a quiz locked to
            // English shows in English and the next reload has them.
            if (!routeState) {
              navigate("/host/question", {
                replace: true,
                state: {
                  ...state,
                  languageMode: m.languageMode,
                  availableLanguages: m.availableLanguages,
                },
              });
            }
          }
          break;
        case "ERROR":
          // The server refuses a Start for a question that is not the next
          // one (a stale or raced tab) under INVALID_PAYLOAD; trying again
          // will not help, so say the quiz moved on instead of the generic
          // banner. The message text is the only thing that tells it apart.
          if (m.code === "INVALID_PAYLOAD" && m.message === "Not the next question") {
            setError("host.errors.question_moved_on");
            break;
          }
          setError(
            serverErrorKey(m, {
              overrides: {
                NOT_TEACHER: "host.errors.taken_over",
                BAD_HOST_TOKEN: "host.errors.no_token",
                STALE_CLIENT: "host.errors.stale_question",
              },
            }),
          );
          break;
        default:
          break;
      }
    }
  }, [navigate, ws, state, routeState]);

  const handleEndQuestion = useCallback(() => {
    if (!round) return;
    ws.send({ type: "END_QUESTION", questionIndex: round.questionIndex });
  }, [round, ws]);

  const handleNext = useCallback(() => {
    if (!round || !state) return;
    const nextIdx = round.questionIndex + 1;
    if (nextIdx >= round.totalQuestions) {
      ws.send({ type: "END_SESSION" });
      return;
    }
    ws.send({ type: "START_QUESTION", questionIndex: nextIdx });
  }, [round, state, ws]);

  const isLastQuestion = round
    ? round.questionIndex === round.totalQuestions - 1
    : false;

  const maxResultCount = useMemo(() => {
    if (!round) return 1;
    return Math.max(1, ...round.results.map((r) => r.count));
  }, [round]);

  // Watchdog, as in the lobby: a catch-up that never comes (no
  // SESSION_JOINED, or one without `host` from an older server or a
  // missing questions row) must not leave the teacher on "Connecting…".
  const waiting = Boolean(state) && !round && !error;
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => {
      setError("host.errors.unreachable");
    }, 5000);
    return () => clearTimeout(timer);
  }, [waiting]);

  if (waiting) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div className="inline-flex items-center gap-3 text-white/70">
          <span className="w-3 h-3 rounded-full bg-brand-green animate-pulseSoft" />
          <span>{t("host.lobby.connecting")}</span>
        </div>
      </main>
    );
  }

  if (!state || !round) {
    return (
      <main className="min-h-full flex items-center justify-center px-6">
        <div className="text-center">
          {error && <HostErrorBanner message={t(error)} onDismiss={() => setError(null)} />}
          <p className="text-white/70">{t("host.question.none")}</p>
          <Button
            onClick={() => navigate("/host")}
            className="mt-4 bg-brand-teal hover:brightness-110"
          >
            {t("host.back_to_review")}
          </Button>
        </div>
      </main>
    );
  }

  const ended = round.phase === "ended";

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-6xl mx-auto">
      <ScreenHeader
        available={state.availableLanguages}
        trailing={
          <div className="text-sm text-white/60">
            {t("host.question.header", {
              index: round.questionIndex + 1,
              total: round.totalQuestions,
            })}{" "}
            ·
            <span className="ml-1">
              {t("host.question.level", { level: round.question.level })}
            </span>
          </div>
        }
      />

      {error && <HostErrorBanner message={t(error)} onDismiss={() => setError(null)} />}

      <section className="text-center mb-8">
        <h1 className="text-2xl md:text-4xl font-bold leading-tight max-w-3xl mx-auto">
          {pickLocalized(round.question.text, displayLang)}
        </h1>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {round.question.options.map((opt, i) => {
              const isCorrect = ended ? round.correctOids.includes(opt.oid) : null;
              return (
                <AnswerTile
                  key={opt.oid}
                  index={i}
                  text={pickLocalized(opt.text, displayLang)}
                  disabled
                  correct={isCorrect}
                />
              );
            })}
          </div>

          {ended && (
            <div className="bg-brand-ink2 rounded-2xl p-5 space-y-3 animate-slideUp">
              <h3 className="font-semibold mb-2">
                {t("host.question.answers_heading")}
              </h3>
              {round.question.options.map((opt, i) => {
                const letter = TILE_LETTERS[i % TILE_LETTERS.length] ?? "?";
                const count =
                  round.results.find((r) => r.oid === opt.oid)?.count ?? 0;
                const ratio = (count / maxResultCount) * 100;
                const correct = round.correctOids.includes(opt.oid);
                return (
                  <div key={opt.oid} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md inline-flex items-center justify-center text-xs font-bold bg-white/15">
                      {letter}
                    </div>
                    <div className="flex-1">
                      <div className="h-3 bg-brand-ink3 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${correct ? "bg-brand-good" : "bg-brand-bad/70"}`}
                          style={{
                            width: `${ratio}%`,
                            transition: "width 600ms ease-out",
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm tabular-nums w-10 text-right">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <TopTimerBar
            // A new timer per round: a catch-up repeating an expired round
            // (its END_QUESTION lost with the old socket) must fire again.
            key={`${round.questionIndex}:${round.startedAt}`}
            totalMs={round.timeLimitMs}
            startedAt={round.startedAt}
            onComplete={ended ? undefined : handleEndQuestion}
            paused={ended}
          />

          {round.answeredCount !== null && (
            <div className="bg-brand-ink2 rounded-2xl p-5">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-sm text-white/60">
                  {t("host.question.answered")}
                </div>
                <div className="text-sm tabular-nums">
                  {round.answeredCount} / {round.totalPlayers || "—"}
                </div>
              </div>
              <div className="h-2 bg-brand-ink3 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-teal transition-all duration-300"
                  style={{
                    width: round.totalPlayers
                      ? `${(round.answeredCount / round.totalPlayers) * 100}%`
                      : "0%",
                  }}
                />
              </div>
            </div>
          )}

          {ended && round.leaderboard.length > 0 && (
            <div className="bg-brand-ink2 rounded-2xl p-5">
              <h3 className="font-semibold mb-3">
                {t("host.question.top_n", { count: 5 })}
              </h3>
              <Leaderboard entries={round.leaderboard.slice(0, 5)} compact />
            </div>
          )}

          {!ended ? (
            <Button variant="secondary" block onClick={handleEndQuestion}>
              {t("host.question.end_question")}
            </Button>
          ) : (
            <Button
              size="lg"
              block
              onClick={handleNext}
              className="bg-brand-amber text-brand-ink font-bold hover:brightness-110 animate-float shadow-2xl shadow-brand-amber/30"
            >
              {isLastQuestion
                ? t("host.question.see_results")
                : t("host.question.next_question")}
            </Button>
          )}
        </aside>
      </div>
    </main>
  );
}
